import type { PayloadAction } from '@reduxjs/toolkit';
import type { ForgotPasswordRequest } from '@/domains/auth/types/api/requests/ForgotPasswordRequest';
import type { ForgotUsernameRequest } from '@/domains/auth/types/api/requests/ForgotUsernameRequest';
import type { OtpSignInRequest } from '@/domains/auth/types/api/requests/OtpSignInRequest';
import type { SignInRequest } from '@/domains/auth/types/api/requests/SignInRequest';
import type { UpdatePasswordRequest } from '@/domains/auth/types/api/requests/UpdatePasswordRequest';
import type { OtpConfigurationResponse } from '@/domains/auth/types/api/responses/OtpConfigurationResponse';
import type { SignInResponse } from '@/domains/auth/types/api/responses/SignInResponse';
import type { HttpServiceResponse } from '@/services/HttpService/types';
import { call, delay, put, select, spawn, takeLatest } from 'redux-saga/effects';
import { AxiosError } from 'axios';
import { EditModeState } from '@/features/editMode/enums/EditModeState';
import { selectIsInEditMode } from '@/features/editMode/store/editModeSelectors';
import { closeEditMode } from '@/features/editMode/store/editModeSlice';
import { closeAllModals } from '@/features/serviceModal/store/serviceModalSlice';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { AuthApiPaths } from '@/domains/auth/enums/AuthApiPaths';
import AuthEvents from '@/domains/auth/events';
import {
  forgotPassword,
  forgotPasswordSuccess,
  forgotUsername,
  forgotUsernameSuccess,
  getOtpConfigIn,
  getOtpConfigInSuccess,
  logout,
  otpSignIn,
  otpSignInSuccess,
  refresh,
  resetAuthStatuses,
  runCycleTokenChecker,
  signIn,
  signInSuccess,
  updatePassword,
  updatePasswordSuccess,
} from '@/domains/auth/store/authSlice';
import { resetUserData } from '@/domains/user/store/userSlice';
import { clearAuthData } from '@/domains/user/utils/clearAuthData';
import { LocalStorageVariables } from '@/enums/LocalStorageVariables';
import { ApiDomainPaths } from '@/enums/apiDomainPaths';
import { RootPaths } from '@/routes/paths';
import EventService from '@/services/EventService/EventService';
import HttpService from '@/services/HttpService/HttpService';
import NavigationService from '@/services/NavigationService/NavigationService';
import ServerEventService from '@/services/ServerEventService/ServerEventService';
import SnackService from '@/services/SnackService/SnackService';
import TokenService from '@/services/TokenService/TokenService';

export function* authUpdatePasswordSaga({ payload }: PayloadAction<UpdatePasswordRequest>) {
  try {
    yield call(HttpService.post, AuthApiPaths.UPDATE_PASSWORD, payload);
    yield put(updatePasswordSuccess());
    yield put(signIn({ username: payload.userName, password: payload.newPassword }));
  } catch (error) {
    console.error(error);
    yield put(resetAuthStatuses());
  }
}

export function* authForgotPasswordSaga({ payload }: PayloadAction<ForgotPasswordRequest>) {
  try {
    yield call(HttpService.post, AuthApiPaths.FORGOT_PASSWORD, payload);
    yield put(forgotPasswordSuccess());

    EventService.dispatch(new AuthEvents.RedirectToForgotPasswordEmail({}));
  } catch (error) {
    console.error(error);
    yield put(resetAuthStatuses());
    EventService.dispatch(new AuthEvents.ForgotUserNameError({}));
  }
}

export function* authForgotUsernameSaga({ payload }: PayloadAction<ForgotUsernameRequest>) {
  try {
    yield call(HttpService.post, AuthApiPaths.FORGOT_USERNAME, payload);
    yield put(forgotUsernameSuccess());

    EventService.dispatch(new AuthEvents.RedirectToForgotUserNameEmail({}));
  } catch (error) {
    console.error(error);
    yield put(resetAuthStatuses());
    EventService.dispatch(new AuthEvents.ForgotUserNameError({}));
  }
}

export function* authSignInSaga({ payload }: PayloadAction<SignInRequest>) {
  try {
    const { data }: HttpServiceResponse<SignInResponse> = yield call(HttpService.post, AuthApiPaths.SIGN_IN, payload);

    yield put(signInSuccess());

    if (!data?.jwtToken || !data?.refreshToken || !data?.userInactivityPeriod)
      EventService.dispatch(new AuthEvents.RedirectToOtp(payload.username));
    else {
      TokenService.setTokenData({
        token: data.jwtToken,
        refreshToken: data.refreshToken,
        expiresAt: data.expiresAt,
        userInactivityPeriod: data.userInactivityPeriod,
      });
      EventService.dispatch(new AuthEvents.RedirectToDefault({}));
    }
  } catch (error) {
    if (error instanceof AxiosError) {
      if (error.response && error.response.data?.isPasswordChangeRequired) {
        localStorage.setItem(LocalStorageVariables.IsRequiredPasswordChange, 'required');
        EventService.dispatch(new AuthEvents.RedirectToUpdatePassword({}));
      }
    }
  }
  yield put(resetAuthStatuses());
}

export function* authOtpSignInSaga({ payload }: PayloadAction<OtpSignInRequest>) {
  try {
    const { data }: HttpServiceResponse<SignInResponse> = yield call(
      HttpService.post,
      AuthApiPaths.OTP_SIGN_IN,
      payload,
    );

    yield put(otpSignInSuccess());

    if (!data?.jwtToken || !data?.refreshToken || !data?.userInactivityPeriod)
      EventService.dispatch(new AuthEvents.RedirectToSignIn({}));
    else {
      TokenService.setTokenData({
        token: data.jwtToken,
        refreshToken: data.refreshToken,
        expiresAt: data.expiresAt,
        userInactivityPeriod: data.userInactivityPeriod,
      });
      if (data?.isPasswordChangeRequired) EventService.dispatch(new AuthEvents.RedirectToUpdatePassword({}));
      else EventService.dispatch(new AuthEvents.RedirectToDefault({}));
    }
  } catch (error) {
    console.error(error);
    EventService.dispatch(new AuthEvents.RedirectToSignIn({}));
    yield put(resetAuthStatuses());
  }
}

export function* authGetOtpConfigInSaga() {
  try {
    const { data }: HttpServiceResponse<OtpConfigurationResponse> = yield call(
      HttpService.get,
      AuthApiPaths.LOGIN_CONFIGURATION,
    );

    yield put(getOtpConfigInSuccess(data));
  } catch (error) {
    console.error(error);
    yield put(resetAuthStatuses());
  }
}

export function* tokenAutomaticCheckerSaga() {
  const token = TokenService.getTokenRaw();
  const isExpired = TokenService.checkTokenIsExpired();

  if (token && isExpired) {
    return yield spawn(logoutSaga);
  }

  yield delay(1000);
  yield call(tokenAutomaticCheckerSaga);
}

export function* logoutSaga() {
  const token = TokenService.getTokenRaw();
  const isInEditMode = yield select(selectIsInEditMode);

  try {
    let status: number | null = null;

    if (token) {
      const response: HttpServiceResponse<void> = yield call(
        HttpService.post,
        `${ApiDomainPaths.ACCOUNT}/logout`,
        {},
        {
          params: {
            requestToken: token,
          },
        },
      );
      status = response.status;
    }

    if (status === 200 || !token) {
      if (isInEditMode) {
        yield put(closeEditMode({ newState: EditModeState.Canceled, shouldCloseAllConnections: true }));
      }
      clearAuthData();
      yield put(resetUserData());
      ServerEventService.disconnectAll();
      NavigationService.navigateTo(RootPaths.SIGN_IN, { replace: true });
      yield put(closeAllModals());
    }
  } catch (error: any) {
    console.error(error);
    SnackService.add({
      type: SnackType.Error,
      title: 'Logout Error',
      content: 'Log out failed. Please try again.',
    });
  }
}

export function* refreshSaga() {
  try {
    const refreshToken = TokenService.getRefreshTokenRaw();

    if (!refreshToken) {
      return yield put(logout());
    }

    const { data }: HttpServiceResponse<SignInResponse> = yield call(
      HttpService.post,
      AuthApiPaths.REFRESH,
      {},
      {
        params: {
          token: refreshToken,
        },
      },
    );

    if (!data?.jwtToken || !data?.refreshToken || !data?.userInactivityPeriod) {
      yield put(logout());
    } else {
      TokenService.setTokenData({
        token: data.jwtToken,
        refreshToken: data.refreshToken,
        expiresAt: data.expiresAt,
        userInactivityPeriod: data.userInactivityPeriod,
      });
    }
  } catch (error) {
    console.error('Failed to refresh token:', error);

    SnackService.add({
      type: SnackType.Error,
      title: 'Token Refresh Error',
      content: 'Failed to refresh the session. Please log in again.',
      anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
    });

    yield put(logout());
  }
}

export function* authWatcher() {
  yield takeLatest(updatePassword.type, authUpdatePasswordSaga);
  yield takeLatest(forgotPassword.type, authForgotPasswordSaga);
  yield takeLatest(otpSignIn.type, authOtpSignInSaga);
  yield takeLatest(signIn.type, authSignInSaga);
  yield takeLatest(getOtpConfigIn.type, authGetOtpConfigInSaga);
  yield takeLatest(forgotUsername.type, authForgotUsernameSaga);
  yield takeLatest(logout.type, logoutSaga);
  yield takeLatest(refresh.type, refreshSaga);
  yield takeLatest(runCycleTokenChecker.type, tokenAutomaticCheckerSaga);
}
